"use client";

import { ReactNode, useEffect, useState } from "react";
import {
  ScoreRow,
  fetchAggregateScore,
  fetchLeaderboard,
} from "@/lib/leaderboard";
import { LeaderboardBoard } from "@/lib/rng";
import PlayerName from "@/components/PlayerName";
import RunDetailModal from "@/components/RunDetailModal";

interface LeaderboardContentsProps {
  // How many entries to show per board (the fetch always pulls the top 100).
  maxEntries?: number;
  // Optional slot beneath the entries (e.g. a "View all" link).
  footer?: ReactNode;
}

// The shared body of every leaderboard surface: the two team totals double as
// Hero / Villain tabs, and the selected board's ranked entries show below.
// Used by both the welcome-screen drawer and the in-game panel so they stay in
// lockstep with the full /leaderboard page.
export default function LeaderboardContents({
  maxEntries = 10,
  footer,
}: LeaderboardContentsProps) {
  const [board, setBoard] = useState<LeaderboardBoard>("hero");
  const [hero, setHero] = useState<ScoreRow[]>([]);
  const [villain, setVillain] = useState<ScoreRow[]>([]);
  const [heroTotal, setHeroTotal] = useState<number | null>(null);
  const [villainTotal, setVillainTotal] = useState<number | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  // The entry whose full run is open in the detail modal (null = closed).
  const [selected, setSelected] = useState<ScoreRow | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchLeaderboard("hero"),
      fetchLeaderboard("villain"),
      fetchAggregateScore("hero"),
      fetchAggregateScore("villain"),
    ])
      .then(([h, v, ht, vt]) => {
        if (cancelled) return;
        setHero(h);
        setVillain(v);
        setHeroTotal(ht);
        setVillainTotal(vt);
        setStatus("ready");
      })
      .catch(() => {
        if (!cancelled) setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const isVillainBoard = board === "villain";
  const entries = (isVillainBoard ? villain : hero).slice(0, maxEntries);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Team totals — also act as the Hero / Villain selector */}
      <div className="grid shrink-0 grid-cols-2 gap-2 px-4 pt-4">
        <button
          onClick={() => setBoard("hero")}
          className={`rounded-xl border px-3 py-2 text-center transition ${
            board === "hero"
              ? "border-accent bg-accent/10"
              : "border-white/10 hover:border-white/30"
          }`}
        >
          <div className="text-[0.6rem] font-bold uppercase tracking-widest text-accent">
            Hero Total
          </div>
          <div className="mt-0.5 font-mono text-lg font-extrabold">
            {heroTotal === null ? "—" : heroTotal.toLocaleString()}
          </div>
        </button>
        <button
          onClick={() => setBoard("villain")}
          className={`rounded-xl border px-3 py-2 text-center transition ${
            board === "villain"
              ? "border-red-500 bg-red-500/10"
              : "border-white/10 hover:border-white/30"
          }`}
        >
          <div className="text-[0.6rem] font-bold uppercase tracking-widest text-red-400">
            Villain Total
          </div>
          <div className="mt-0.5 font-mono text-lg font-extrabold">
            {villainTotal === null ? "—" : villainTotal.toLocaleString()}
          </div>
        </button>
      </div>

      <div className="shrink-0 px-4 pb-2 pt-3 text-center text-[0.65rem] font-bold uppercase tracking-[0.3em] text-white/40">
        {isVillainBoard ? "Villain Leaderboard" : "Hero Leaderboard"}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        {status === "loading" && (
          <p className="py-8 text-center text-sm text-white/40">Loading…</p>
        )}
        {status === "error" && (
          <p className="py-8 text-center text-sm text-white/40">
            Couldn&apos;t load scores.
          </p>
        )}
        {status === "ready" && entries.length === 0 && (
          <p className="py-8 text-center text-sm text-white/40">
            No {isVillainBoard ? "villains" : "heroes"} yet — be the first!
          </p>
        )}
        {status === "ready" && entries.length > 0 && (
          <div className="flex flex-col gap-2">
            {entries.map((entry, i) => (
              <button
                key={entry.id}
                onClick={() => setSelected(entry)}
                className="group flex w-full items-center gap-3 rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-left transition hover:border-accent/50"
              >
                <div className="w-6 shrink-0 text-center font-mono text-sm font-bold text-white/40">
                  {i + 1}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <PlayerName
                      name={entry.name}
                      title={entry.title}
                      color={entry.color}
                      nameClassName="text-white"
                    />
                    {entry.has_crown && (
                      <span className="rounded-full border border-yellow-400/60 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-widest text-yellow-300">
                        Crowned
                      </span>
                    )}
                    {entry.is_sidekick && (
                      <span className="rounded-full border border-white/20 px-1.5 py-0.5 text-[0.55rem] font-bold uppercase tracking-widest text-white/50">
                        Sidekick
                      </span>
                    )}
                  </div>
                  <div className="text-[0.65rem] uppercase tracking-widest text-white/40">
                    {entry.rank_label}
                  </div>
                </div>
                {/* Score, swapped for a "View" pill on hover (desktop). Tapping
                    the row anywhere opens the run on touch too. */}
                <div className="relative shrink-0">
                  <div
                    className={`font-mono text-base font-extrabold transition group-hover:opacity-0 ${
                      isVillainBoard ? "text-red-400" : "text-accent"
                    }`}
                  >
                    {entry.score.toLocaleString()}
                  </div>
                  <div className="pointer-events-none absolute inset-0 flex items-center justify-end opacity-0 transition group-hover:opacity-100">
                    <span className="rounded-full bg-accent px-3 py-1 text-[0.6rem] font-bold uppercase tracking-widest text-white">
                      View
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {footer}

      <RunDetailModal entry={selected} onClose={() => setSelected(null)} />
    </div>
  );
}
